import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useFieldArray, useForm } from "react-hook-form";
import { QuestionSet, setSchema } from ".././types";
import createAxios from "@/lib/axios";
import { toast } from "sonner";
import { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";

function QuestionSetEditor() {
  const { questionSet } = Route.useLoaderData();
  return <ViewQuestionSet defaultValues={questionSet} />;
}

export const Route = createFileRoute("/dashboard/evaluate/question-set/$id")({
  component: QuestionSetEditor,
  loader: async ({ params: { id } }) => {
    const questionSet = await GetQuestionSetById(id);
    return { id, questionSet };
  },
});

interface QuestionProps {
  index: number;
  field: FieldArrayWithId<QuestionSet, "questions">;
  form: UseFormReturn<QuestionSet>;
  remove: (index: number) => void;
}

async function GetQuestionSetById(
  id: string,
): Promise<QuestionSet | undefined> {
  try {
    const client = await createAxios();
    const response = await client.get(
      `${import.meta.env.VITE_BACKEND_URL}/question-set/${id}`,
    );
    return response.data;
  } catch (error) {
    toast.error("Failed to fetch question set");
    throw error;
  }
}

interface ViewQuestionSetProps {
  defaultValues?: QuestionSet;
}

function ViewQuestionSet({ defaultValues }: ViewQuestionSetProps) {
  const form = useForm<QuestionSet>({
    resolver: zodResolver(setSchema),
    defaultValues: defaultValues,
  });

  const { fields } = useFieldArray({
    control: form.control,
    name: "questions",
  });

  const navigate = useNavigate();

  const handleStart = async () => {
    navigate({ to: "/dashboard/evaluate/new" });
  };

  return (
    <div className="size-full bg-card px-4 py-8 text-card-foreground">
      <Form {...form}>
        <div className="w-full space-y-4">
          <h2 className="text-2xl font-bold">View Question Set</h2>
          <FormField
            control={form.control}
            name={`name`}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="text"
                    className="max-w-96 bg-card text-card-foreground"
                    placeholder="Enter name"
                    {...field}
                    readOnly
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {fields.map((field, index) => (
            <Question
              key={field.id}
              index={index}
              field={field}
              form={form}
              remove={() => {}}
            />
          ))}
        </div>
      </Form>
      <div className="flex justify-center">
        <Button variant="outline2" className="mt-4" onClick={handleStart}>
          Start Evaluation
        </Button>
      </div>
    </div>
  );
}

function Question({ index, field, form }: QuestionProps) {
  return (
    <div>
      <Card key={field.id} className="relative">
        <CardHeader className="flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Question {index + 1}</CardTitle>
        </CardHeader>
        <CardContent className="w-full space-y-4">
          <div className="flex w-full flex-row gap-6">
            <FormField
              control={form.control}
              name={`questions.${index}.marks`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Marks</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      className="max-w-24 bg-gray-200 text-card-foreground"
                      min={1}
                      {...field}
                      readOnly
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`questions.${index}.question`}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Question</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter the question"
                      className="min-h-24 bg-gray-200 text-card-foreground"
                      {...field}
                      readOnly
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex w-full flex-row gap-6">
            <FormField
              control={form.control}
              name={`questions.${index}.reference_answer`}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Reference Answer</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter the reference answer"
                      className="min-h-24 bg-gray-200 text-card-foreground"
                      {...field}
                      readOnly
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`questions.${index}.rubrics`}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Rubrics</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter the rubrics"
                      className="min-h-24 bg-gray-200 text-card-foreground"
                      {...field}
                      readOnly
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </CardContent>
        <CardFooter className="flex justify-end pt-0"></CardFooter>
      </Card>
    </div>
  );
}
